import { useState } from "react";
import { changePassword } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordCard({ userRecordId }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!next) return toast.error("New password required");
    if (next.length < 6) return toast.error("New password must be at least 6 chars");
    if (next !== confirm) return toast.error("Passwords don't match");
    setSaving(true);
    try {
      await changePassword(userRecordId, next);
      toast.success("Password updated");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="rounded-2xl border-zinc-200 p-4" data-testid="change-password-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
          <Lock className="text-violet-700" size={18} />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-zinc-900">Change password</div>
          <p className="text-xs text-zinc-500 mt-0.5">
            Set a new password — no need to re-enter the current one.
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-2.5">
        <div>
          <Label className="text-xs text-zinc-700">New password</Label>
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="h-10 rounded-lg mt-1"
            autoComplete="new-password"
            data-testid="cp-new-input"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-700">Confirm new password</Label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="h-10 rounded-lg mt-1"
            autoComplete="new-password"
            data-testid="cp-confirm-input"
          />
        </div>
        <Button
          type="submit"
          disabled={saving}
          className="w-full mt-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-11"
          data-testid="cp-submit-btn"
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : "Update password"}
        </Button>
      </form>
    </Card>
  );
}
